import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { tablePadding, spacing, layoutUtils } from '@/lib/layout-utils';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { 
  MoreHorizontal, 
  Edit, 
  GraduationCap,
  Heart,
  Calendar,
  User,
  Cake,
  BookOpen,
  Eye,
  UserPlus,
  Users
} from 'lucide-react';
import { Student } from '@/types/models';

interface StudentTableRowProps {
  student: Student;
  familyName?: string;
  enrollmentCount: number;
  onClick: () => void;
  onEdit: (student: Student) => void;
  onViewFamily: (student: Student) => void;
  onViewEnrollments: (student: Student) => void;
  onAssignToFamily?: (student: Student) => void;
  onEnrollInClass?: (student: Student) => void;
}

const StudentTableRow: React.FC<StudentTableRowProps> = ({
  student,
  familyName,
  enrollmentCount,
  onClick,
  onEdit,
  onViewFamily,
  onViewEnrollments,
  onAssignToFamily,
  onEnrollInClass
}) => {
  const getAge = () => {
    if (!student.date_of_birth) return null;
    const birthDate = new Date(student.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const getAgeGroup = (age: number | null) => {
    if (!age) return { label: 'Unknown', variant: 'outline' as const };
    if (age <= 10) return { label: 'Young', variant: 'secondary' as const };
    if (age <= 17) return { label: 'Teen', variant: 'default' as const };
    return { label: 'Adult', variant: 'destructive' as const };
  };

  const getEnrollmentStatus = () => {
    if (enrollmentCount === 0) {
      return { label: 'Not Enrolled', variant: 'outline' as const };
    } else if (enrollmentCount === 1) {
      return { label: '1 Class', variant: 'default' as const };
    } else {
      return { label: `${enrollmentCount} Classes`, variant: 'default' as const };
    }
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || 'S';
    const last = lastName?.charAt(0) || 'T';
    return `${first}${last}`.toUpperCase();
  };

  const isRecentBirthday = () => {
    if (!student.date_of_birth) return false;
    const birthDate = new Date(student.date_of_birth);
    const today = new Date();
    
    // Check if birthday is within the last 7 days or next 7 days
    const thisYearBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    const daysDiff = Math.abs((today.getTime() - thisYearBirthday.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysDiff <= 7;
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on interactive elements
    if ((e.target as HTMLElement).closest('button, [role="button"]')) {
      return;
    }
    onClick();
  };

  const age = getAge();
  const ageGroup = getAgeGroup(age);
  const enrollmentStatus = getEnrollmentStatus();
  const recentBirthday = isRecentBirthday();

  return (
    <>
      {/* Student Info */}
      <td className={layoutUtils.getTableRowClasses(true, tablePadding.cell)} onClick={handleRowClick}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback className="bg-gradient-to-br from-purple-100 to-blue-100 text-purple-700">
                {getInitials(student.first_name, student.last_name)}
              </AvatarFallback>
            </Avatar>
            {recentBirthday && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-pink-400 to-red-500 rounded-full flex items-center justify-center">
                <Cake className="h-2 w-2 text-white" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate hover:text-primary transition-colors">
              {student.first_name || 'Unknown'} {student.last_name || 'Student'}
            </div>
            {student.preferred_name && student.preferred_name !== student.first_name && (
              <div className="text-sm text-muted-foreground truncate">
                "{student.preferred_name}"
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Family */}
      <td className={layoutUtils.getTableRowClasses(true, tablePadding.cell)} onClick={handleRowClick}>
        <div className="min-w-0">
          {familyName ? (
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-pink-500" />
              <span className="text-sm truncate hover:text-primary transition-colors">
                {familyName} Family
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-4 w-4" />
              <span className="text-sm">No family assigned</span>
            </div>
          )}
        </div>
      </td>

      {/* Age & Age Group */}
      <td className={layoutUtils.getTableRowClasses(true, tablePadding.cell)} onClick={handleRowClick}>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-blue-500" />
            <span>{age !== null ? `${age} years` : 'Unknown'}</span>
          </div>
          <Badge variant={ageGroup.variant} className="text-xs">
            {ageGroup.label}
          </Badge>
        </div>
      </td>

      {/* Enrollments */}
      <td className={layoutUtils.getTableRowClasses(true, tablePadding.cell)} onClick={handleRowClick}>
        <div className="flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-green-500" />
          <Badge variant={enrollmentStatus.variant} className="text-xs">
            <BookOpen className="h-3 w-3 mr-1" />
            {enrollmentStatus.label}
          </Badge>
        </div>
      </td>

      {/* Actions */}
      <td className={layoutUtils.getTableRowClasses(false, tablePadding.cell)}>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(student);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Student
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {enrollmentCount > 0 ? (
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewEnrollments(student);
                  }}
                >
                  <GraduationCap className="h-4 w-4 mr-2" />
                  View Classes ({enrollmentCount})
                </DropdownMenuItem>
              ) : (
                onEnrollInClass && (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      onEnrollInClass(student);
                    }}
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Enroll in Class
                  </DropdownMenuItem>
                )
              )}
              
              <DropdownMenuSeparator />
              
              {familyName ? (
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewFamily(student);
                  }}
                >
                  <Heart className="h-4 w-4 mr-2" />
                  View {familyName} Family
                </DropdownMenuItem>
              ) : (
                onAssignToFamily && (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignToFamily(student);
                    }}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Assign to Family
                  </DropdownMenuItem>
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </>
  );
};

export default StudentTableRow;